import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { FeatureViewer } from 'feature-viewer-typescript';
import 'feature-viewer-typescript/src/styles/styles.scss';
import { Dialog } from 'primereact/dialog';

const parsePosition = (posString) => {
  if (typeof posString === 'number') return posString;
  if (typeof posString !== 'string') return null;
  const parts = posString.split('_');
  const num = parseInt(parts[parts.length - 1], 10);
  return Number.isNaN(num) ? null : num;
};

const generateUniqueId = () =>
  Array.from({ length: 8 }, () =>
    String.fromCharCode(97 + Math.floor(Math.random() * 26))
  ).join('');

const groupIntoRanges = (sortedData, field, trackName, getColor) => {
  const ranges = [];
  let current = null;
  sortedData.forEach((row) => {
    const value = row[field];
    const pos = parsePosition(row.anPosition);
    if (pos == null) return;
    if (value && value !== '-' && value !== '') {
      if (current && current.value === value) {
        current.y = pos;
      } else {
        if (current) ranges.push(current);
        const color = getColor(value);
        current = {
          x: pos, y: pos, value,
          description: `${trackName}: ${value}`, tooltip: `${trackName}: ${value}`,
          id: generateUniqueId(), color: color,
        };
      }
    } else if (current) {
      ranges.push(current);
      current = null;
    }
  });
  if (current) ranges.push(current);
  return ranges.map(({ value, ...rest }) => rest);
};

const getPlddtColor = (score) => {
  if (score >= 90) return '#0053d6';
  if (score >= 70) return '#65cbf3';
  if (score >= 50) return '#ffdb13';
  return '#ff7d45';
};

const getAlphaMissenseColor = (score) => {
  if (score >= 0.564) return '#db0909';
  if (score > 0.340)  return '#f1c40f';
  return '#0984e3';
};

const getNestedValue = (obj, path) => {
  return path.split('.').reduce((acc, part) => acc && acc[part], obj);
};

// phosPtms — 8 specific types + multi-PTM sentinel
const PTM_COLOR_MAP = {
  phosphorylation:          '#4393C3',
  acetylation:              '#5aae61',
  ubiquitination:           '#bf812d',
  sumoylation:              '#d6604d',
  methylation:              '#c51b7d',
  'o-galnac glycosylation': '#e08214',
  'o-glcnac glycosylation': '#fdb863',
  other:                    '#666666',
};

// PhosphoSitePlus short-code aliases
const PTM_SHORT_CODE_MAP = {
  p:  '#4393C3',
  ac: '#5aae61',
  ub: '#bf812d',
  sm: '#d6604d',
  m:  '#c51b7d',
  ga: '#e08214',
  gl: '#fdb863',
};
const PTM_MULTI_COLOR = '#66D8C9'; // "Multiple PTMs"

// unGlycosylations — N/O/C/S-linked
const GLYCOSYLATION_KEYWORDS = [
  { key: 'n-linked', color: '#56ab2f' },
  { key: 'o-linked', color: '#9d4edd' },
  { key: 'c-linked', color: '#d90368' },
  { key: 's-linked', color: '#00b4d8' },
];
const GLYCOSYLATION_DEFAULT_COLOR = '#56ab2f';

// unLipidations — 10 subtypes
// IMPORTANT: longer keywords that share a prefix with shorter ones come first.
const LIPIDATION_KEYWORDS = [
  { key: 'palmitoleyl',    color: '#1d3557' }, // O-palmitoleyl serine
  { key: 'palmitoyl',      color: '#007f5f' }, // S-palmitoyl cysteine
  { key: 'myristoyl',      color: '#2a9d8f' }, // N-myristoyl glycine
  { key: 'geranylgeranyl', color: '#f4a261' }, // S-geranylgeranyl cysteine (before 'farnesyl')
  { key: 'farnesyl',       color: '#e9c46a' }, // S-farnesyl cysteine
  { key: 'gpi',            color: '#264653' }, // GPI-anchor
  { key: 'cholesterol',    color: '#e76f51' }, // Cholesterol ester
  { key: 'lipoyl',         color: '#8ab17d' }, // Lipoylation
  { key: 'stearoyl',       color: '#457b9d' }, // S-stearoyl cysteine
  { key: 'octanoyl',       color: '#6a4c93' }, // O-octanoyl serine
];
const LIPIDATION_DEFAULT_COLOR = '#007f5f';

// unMoleculeProcessing — accepts both short UniProt codes and full PSI-MOD labels
const MOLECULE_PROCESSING_KEYWORDS = [
  { key: 'initiator methionine', color: '#20b2aa' },
  { key: 'active peptide',       color: '#f3722c' },
  { key: 'signal peptide',       color: '#da54a3' },
  { key: 'transit peptide',      color: '#820e44' },
  { key: 'propeptide',           color: '#c746d4' },
  { key: 'mature protein',       color: '#838ffc' },
];
const MOLECULE_PROCESSING_SHORT = {
  CHAIN:    '#838ffc',
  SIGNAL:   '#da54a3',
  TRANSIT:  '#820e44',
  'PRO_*':  '#c746d4',
  INIT_MET: '#20b2aa',
  PEPTIDE:  '#f3722c',
};

// unLocation — accepts both short codes and full labels
const LOCATION_KEYWORDS = [
  { key: 'topological domain',   color: 'rgb(12, 116, 137)' },
  { key: 'transmembrane region', color: '#da54a3' },
  { key: 'intramembrane region', color: '#820e44' },
];
const LOCATION_SHORT = {
  EXTRAMEM: 'rgb(12, 116, 137)',
  TOPO_DOM: 'rgb(12, 116, 137)',
  TRANSMEM: '#da54a3',
  INTRAMEM: '#820e44',
};

/**
 * Find a color by case-insensitive substring match against an ORDERED
 * keyword list. Returns the first hit.
 */
const matchByKeyword = (value, keywordList, defaultColor) => {
  if (!value) return defaultColor;
  const lower = String(value).toLowerCase();
  const hit = keywordList.find(({ key }) => lower.includes(key));
  return hit ? hit.color : defaultColor;
};

/**
 * Resolve PTM color. Accepts PhosphoSitePlus short codes ("p", "ac-Lys"),
 * PSI-MOD long labels ("Phosphorylation [MOD:00696]"), and multi-PTM cells.
 */
const getPtmColor = (ptmValue) => {
  if (!ptmValue || String(ptmValue).trim() === '-') return PTM_COLOR_MAP.other;
  const trimmed = String(ptmValue).trim();

  // Strip bracketed annotations before deciding multi-vs-single
  const stripped = trimmed.replace(/\[[^\]]*\]/g, '').trim();

  // Semicolons are unambiguous multi-PTM separators
  if (stripped.split(';').filter(Boolean).length > 1) return PTM_MULTI_COLOR;

  // Whitespace only counts as multi if all tokens are short codes
  const wsTokens = stripped.split(/\s+/).filter(Boolean);
  if (wsTokens.length > 1 && wsTokens.every(t => t.length <= 3)) {
    return PTM_MULTI_COLOR;
  }

  const lower = (stripped || trimmed).toLowerCase();

  // Long PSI-MOD label match first
  for (const [label, color] of Object.entries(PTM_COLOR_MAP)) {
    if (label !== 'other' && lower.includes(label)) return color;
  }

  // Fall back to short code
  const head = lower.split('-')[0].trim();
  return PTM_SHORT_CODE_MAP[head] || PTM_COLOR_MAP.other;
};

/* ── Evidence config ── */
const EVIDENCE_STYLES = {
  curated:   { color: '#22c55e', letter: 'C', label: 'Curated' },
  derived:   { color: '#8b5e3c', letter: 'D', label: 'Derived' },
  homology:  { color: '#64748b', letter: 'H', label: 'Homology' },
  predicted: { color: '#a855f7', letter: 'P', label: 'Predicted' },
};

const BADGE_REGEX = /\{\{(curated|derived|homology|predicted)\}\}\s*/;

const EvidenceBadge = ({ type }) => {
  const ev = EVIDENCE_STYLES[type];
  if (!ev) return null;
  return (
    <span
      title={ev.label}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 18, height: 18, borderRadius: 4,
        backgroundColor: ev.color, color: '#fff',
        fontSize: 10, fontWeight: 700, lineHeight: 1,
        marginRight: 4, flexShrink: 0, cursor: 'default',
      }}
    >
      {ev.letter}
    </span>
  );
};

const EvidenceLegend = () => {
  const items = [
    { type: 'curated', label: 'Curated' },
    { type: 'derived', label: 'Derived' },
    { type: 'homology', label: 'Homology' },
    { type: 'predicted', label: 'Predicted' },
  ];
  return (
    <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap', marginTop: 8 }}>
      {items.map((item) => (
        <span key={item.type} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
          <EvidenceBadge type={item.type} />
          {item.label}
        </span>
      ))}
    </div>
  );
};

const FeaturesViewer = ({
  data,
  colors,
  domainInterPro,
  setRange
}) => {
  const containerId = 'fv-container-biocomputingup';
  const [isHelpVisible, setIsHelpVisible] = useState(false);

  const unDomainsColorMap = useMemo(() => {
    if (!colors) return {};
    return Object.fromEntries(
      Object.entries(colors).map(([key, color]) => [
        key,
        typeof color === 'string' && !color.startsWith('#') ? `#${color}` : color
      ])
    );
  }, [colors]);

  const interProColorMap = useMemo(() => {
    if (!domainInterPro) return {};
    return domainInterPro.reduce((map, domain) => {
      map[domain.domainName] = domain.color.startsWith('#') ? domain.color : `#${domain.color}`;
      return map;
    }, {});
  }, [domainInterPro]);

  const trackDefinitions = useMemo(() => [
    { field: 'unAnnNatVariants',       name: 'Natural variants',       color: 'rgb(178, 24, 43)', type: 'circle', evidence: 'curated' },
    { field: 'unLigandsBindingSite',   name: 'Binding site',           color: '#00b159', type: 'unique', evidence: 'curated' },
    { field: 'unAnnPropActiveSite',    name: 'Active site',            color: '#f37735', type: 'unique', evidence: 'curated' },
    { field: 'unModifiedRes',          name: 'Modified residue',       color: '#ffc425', type: 'unique', evidence: 'curated' },
    { field: 'unCrossLinks',           name: 'Cross-links',            color: '#00aedb', type: 'unique', evidence: 'curated' },
    { field: 'pfamDomainPosHmm',       name: 'PFAM-HMM position',      color: '#aa1c7d', type: 'unique', evidence: 'derived' },
    { field: 'unMoleculeProcessing',   name: 'Molecule processing',    type: 'rect', defaultColor: '#838ffc', evidence: 'curated' },
    { field: 'unLocation',             name: 'Location',               type: 'rect', defaultColor: 'rgb(12, 116, 137)', evidence: 'curated' },
    { field: 'unMotives',              name: 'Motifs',                 color: '#e63946', type: 'rect', evidence: 'curated' },
    { field: 'unDomains',              name: 'Domains',                type: 'rect', defaultColor: '#e09f3e', evidence: 'curated' },
    { field: 'interproDomain',         name: 'InterPro',               type: 'rect', defaultColor: '#3a86ff', evidence: 'derived' },
    { field: 'phosPtms',               name: 'PTMs PhosphoSitePlus®',  type: 'lollipop', evidence: 'curated' },
    { field: 'unGlycosylations',       name: 'Glycosylations',         type: 'rect', defaultColor: GLYCOSYLATION_DEFAULT_COLOR, evidence: 'curated' },
    { field: 'unLipidations',          name: 'Lipidations',            type: 'rect', defaultColor: LIPIDATION_DEFAULT_COLOR,   evidence: 'curated' },
    { field: 'unDisulfideBongBridges', name: 'Disulfide bridges',      color: '#8338ec', type: 'rect', evidence: 'curated' },
    { field: 'inBindingRegion',        name: 'Binding region',         color: '#ff006e', type: 'rect', evidence: 'derived' },
    { field: 'afConfidenceValue',      name: 'pLDDT',                  type: 'unique', evidence: 'predicted' },
    { field: 'aamissense.amMean',      name: 'amMean',                 type: 'unique', evidence: 'predicted' },
    { field: 'aamissense.amPathogenicFrac',      name: 'amPathogenicFrac',  type: 'curve', evidence: 'predicted' },
  ], []);

  /**
   * Replace {{type}} placeholders in text nodes with HTML badge overlays.
   * Since SVG clips badges, we use absolutely-positioned HTML divs instead.
   */
  const replaceBadgePlaceholders = useCallback(() => {
    const container = document.getElementById(containerId);
    if (!container) return 0;

    container.querySelectorAll('.ev-badge-overlay').forEach(el => el.remove());
    container.style.position = 'relative';

    const containerRect = container.getBoundingClientRect();
    const badgeSize = 16;

    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null, false);
    const textNodes = [];
    while (walker.nextNode()) textNodes.push(walker.currentNode);

    const matches = [];
    let maxRight = 0;

    textNodes.forEach((node) => {
      const raw = node.nodeValue || '';
      const match = raw.match(BADGE_REGEX);
      if (!match) return;

      const evidenceKey = match[1];
      const ev = EVIDENCE_STYLES[evidenceKey];
      if (!ev) return;

      const cleanText = raw.replace(BADGE_REGEX, '');
      node.nodeValue = cleanText;

      const parentEl = node.parentElement;
      if (!parentEl) return;

      const range = document.createRange();
      range.selectNodeContents(parentEl);
      const textRect = range.getBoundingClientRect();

      const rightEdge = textRect.right - containerRect.left;
      if (rightEdge > maxRight) maxRight = rightEdge;

      matches.push({ evidenceKey, ev, textRect });
    });

    const fixedLeft = maxRight + 35;

    let count = 0;
    matches.forEach(({ ev, textRect }) => {
      const verticalOffset = -3.5;
      const badgeTop =
        textRect.top - containerRect.top + (textRect.height / 2) - (badgeSize / 2) + verticalOffset;

      const badge = document.createElement('div');
      badge.className = 'ev-badge-overlay';
      badge.title = ev.label;
      badge.textContent = ev.letter;
      Object.assign(badge.style, {
        position: 'absolute',
        top: `${badgeTop}px`,
        left: `${fixedLeft}px`,
        width: '18px',
        height: '18px',
        borderRadius: '4px',
        backgroundColor: ev.color,
        color: '#fff',
        fontSize: '10px',
        fontWeight: '700',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: '1',
        pointerEvents: 'none',
        zIndex: '10',
      });

      container.appendChild(badge);
      count++;
    });

    return count;
  }, []);

  useEffect(() => {
    const container = document.getElementById(containerId);
    if (!container) return;
    container.innerHTML = '';

    if (!FeatureViewer || !Array.isArray(data) || data.length === 0) return;

    const sorted = [...data].sort(
      (a, b) => parsePosition(a.anPosition) - parsePosition(b.anPosition)
    );

    const sequence = sorted
      .map((row) => (/[A-Za-z]/.test(row.unRefProtRe) ? row.unRefProtRe : 'X'))
      .join('');

    if (!sequence) return;

    const fv = new FeatureViewer(sequence, `#${containerId}`, {
      showAxis: true, showSequence: true, brushActive: true,
      toolbar: true, bubbleHelp: false, zoomMax: 20,
      flagColor: '#FFFFFF', flagTrack: 200, flagTrackMobile: 100
    });

    const features = trackDefinitions.flatMap((trk) => {
      let dataRows = [];

      if (trk.type === 'rect') {
        const defaultColor = trk.color || trk.defaultColor;
        let getColor = () => defaultColor;

        if (trk.field === 'unDomains') {
          getColor = (value) => unDomainsColorMap[value] || defaultColor;
        } else if (trk.field === 'interproDomain') {
          getColor = (value) => {
            if (!value) return defaultColor;
            const matchingKey = Object.keys(interProColorMap).find(key =>
              value.toLowerCase().includes(key.toLowerCase())
            );
            return matchingKey ? interProColorMap[matchingKey] : defaultColor;
          };
        } else if (trk.field === 'unMoleculeProcessing') {
          getColor = (value) => {
            if (!value) return defaultColor;
            if (MOLECULE_PROCESSING_SHORT[value]) return MOLECULE_PROCESSING_SHORT[value];
            if (value.startsWith('PRO_')) return MOLECULE_PROCESSING_SHORT['PRO_*'];
            return matchByKeyword(value, MOLECULE_PROCESSING_KEYWORDS, defaultColor);
          };
        } else if (trk.field === 'unLocation') {
          getColor = (value) => {
            if (!value) return defaultColor;
            if (LOCATION_SHORT[value]) return LOCATION_SHORT[value];
            return matchByKeyword(value, LOCATION_KEYWORDS, defaultColor);
          };
        } else if (trk.field === 'unGlycosylations') {
          getColor = (value) =>
            matchByKeyword(value, GLYCOSYLATION_KEYWORDS, GLYCOSYLATION_DEFAULT_COLOR);
        } else if (trk.field === 'unLipidations') {
          getColor = (value) =>
            matchByKeyword(value, LIPIDATION_KEYWORDS, LIPIDATION_DEFAULT_COLOR);
        }

        dataRows = groupIntoRanges(sorted, trk.field, trk.name, getColor);

      } else if (trk.type === 'curve') {

        dataRows = sorted.map((row) => {
          const pos = parsePosition(row.anPosition);
          const val = trk.field.includes('.') ? getNestedValue(row, trk.field) : row[trk.field];
          if (pos == null || val == null || val === '-') return null;
          return { id: generateUniqueId(), x: pos, y: parseFloat(val), tooltip: `${trk.name}: ${val}` };
        }).filter(Boolean);

      } else {

        dataRows = sorted.map((row) => {
          const pos = parsePosition(row.anPosition);
          const val = trk.field.includes('.') ? getNestedValue(row, trk.field) : row[trk.field];
          if (pos == null || !val || val === '-') return null;
          const dataPoint = {
            id: generateUniqueId(), x: pos, y: pos, tooltip: `${trk.name}: ${val}`
          };
          if (trk.field === 'phosPtms') {
            dataPoint.color = getPtmColor(val);
          } else if (trk.field === 'afConfidenceValue') {
            const score = parseFloat(val);
            if (!Number.isNaN(score)) dataPoint.color = getPlddtColor(score);
          } else if (trk.field === 'aamissense.amMean') {
            const score = parseFloat(val);
            if (!Number.isNaN(score)) dataPoint.color = getAlphaMissenseColor(score);
          }
          return dataPoint;
        }).filter(Boolean);
      }

      if (!dataRows.length) return [];

      const featureObj = {
        id: generateUniqueId(),
        data: dataRows,
        label: `{{${trk.evidence}}} ${trk.name}`,
        color: trk.color,
        type: trk.type,
        height: 4,
      };

      const dynamicColorTracks = [
        'phosPtms', 'unDomains', 'interproDomain', 'unMoleculeProcessing',
        'unLocation', 'unGlycosylations', 'unLipidations',
        'afConfidenceValue', 'aamissense.amMean', 'aamissense.amPathogenicFrac',
      ];
      if (dynamicColorTracks.includes(trk.field)) {
        delete featureObj.color;
      }

      if (trk.type === 'curve') {
        featureObj.yLim = 100;
      }
      return [featureObj];
    });

    if (features.length) fv.addFeatures(features);

    // Post-process: replace {{type}} placeholders with HTML overlay badges
    const tryInject = (attempt) => {
      if (attempt > 15) return;
      setTimeout(() => {
        const count = replaceBadgePlaceholders();
        if (!count && attempt < 15) {
          tryInject(attempt + 1);
        }
      }, attempt * 100);
    };
    tryInject(0);

    return () => { container.innerHTML = ''; };
  }, [
    data,
    colors,
    domainInterPro,
    trackDefinitions,
    unDomainsColorMap,
    interProColorMap,
    replaceBadgePlaceholders
  ]);

  return (
    <>
      <div id={containerId} className="col-9 img-fluid" style={{ width: '100%' }} />
      <EvidenceLegend />
      <Dialog
        header="Feature Viewer Help"
        visible={isHelpVisible}
        style={{ width: '50vw', maxWidth: '600px', minWidth: '320px' }}
        onHide={() => setIsHelpVisible(false)}
        modal
        dismissableMask
      >
        <div className="p-2">
          <h4 style={{ marginTop: 0 }}>Interaction Guide</h4>
          <ul style={{ paddingLeft: '20px', lineHeight: '1.6' }}>
            <li><strong>Select a Region:</strong> Click and drag horizontally over the sequence or axis. The selected start and end positions will be captured.</li>
            <li><strong>Clear Selection:</strong> Click anywhere on the viewer background to clear the current selection.</li>
            <li><strong>Feature Details:</strong> Hover your mouse over any feature to see a tooltip with its details.</li>
            <li><strong>Toolbar:</strong> Use the buttons on the top right to reset zoom, download the view as an SVG, or open this help dialog.</li>
          </ul>
          <h4>Evidence Types</h4>
          <ul style={{ paddingLeft: '20px', lineHeight: '1.8' }}>
            <li><EvidenceBadge type="curated" /> <strong>Curated</strong> — Manually reviewed annotations from databases like UniProt.</li>
            <li><EvidenceBadge type="predicted" /> <strong>Predicted</strong> — Computationally predicted data (e.g., AlphaFold, AlphaMissense).</li>
            <li><EvidenceBadge type="derived" /> <strong>Derived</strong> — Derived from external databases or computational pipelines (e.g., PFAM, InterPro, IntAct).</li>
            <li><EvidenceBadge type="homology" /> <strong>Homology</strong> — Annotations inferred by homology.</li>
          </ul>
        </div>
      </Dialog>
    </>
  );
};

export default FeaturesViewer;
